import { useState, useRef, useEffect, useCallback } from "react";
import { kanbanStages, formatMoney, type KanbanStage, type KanbanCard } from "@/mocks/managerWorkspace";

function StageColumn({ stage }: { stage: KanbanStage }) {
  return (
    <div className="flex flex-col min-w-[230px] w-[230px] flex-shrink-0 bg-white border border-[#D5CA8A] rounded">
      {/* Stage header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#D5CA8A] bg-[#FFFDF5]">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold whitespace-nowrap ${stage.color}`}>
            <i className={`${stage.icon} text-[11px]`}></i>
            {stage.label}
          </span>
          <span className="text-[11px] text-[#666666] flex-shrink-0">{stage.cards.length}</span>
        </div>
        <button className="w-5 h-5 flex items-center justify-center rounded hover:bg-[#FDF2B5] text-[#666666] cursor-pointer transition-colors flex-shrink-0">
          <i className="ri-add-line text-[12px]"></i>
        </button>
      </div>

      {/* Cards */}
      <div className="flex flex-col gap-2 p-2 min-h-[200px] overflow-y-auto">
        {stage.cards.map((card) => (
          <KanbanCardItem key={card.id} card={card} />
        ))}
        {stage.cards.length === 0 && (
          <div className="flex items-center justify-center h-full min-h-[160px] text-[11px] text-[#999999]">
            Нет сделок
          </div>
        )}
      </div>
    </div>
  );
}

function KanbanCardItem({ card }: { card: KanbanCard }) {
  const isNew = card.daysActive <= 3;
  const isStale = card.daysActive > 10;

  return (
    <div className="bg-[#FFFDF5] border border-[#E8DB6C]/50 rounded p-3 cursor-pointer hover:border-[#C4B96A] hover:bg-[#FFFEF9] transition-colors">
      <div className="flex items-start justify-between mb-1.5">
        <span className="text-[11px] text-[#666666] font-mono">{card.id}</span>
        {isNew && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#D4EDDA] text-[#2D6A4F] font-medium">Новая</span>
        )}
        {isStale && (
          <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#DC2626] font-medium">{card.daysActive} дн</span>
        )}
      </div>

      <p className="text-[12px] font-semibold text-[#2D2D2D] mb-1 leading-tight">{card.client}</p>

      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] text-[#666666] truncate">{card.product}</span>
        <span className="text-[11px] font-semibold text-[#2D2D2D] whitespace-nowrap">{formatMoney(card.amount)}</span>
      </div>

      <div className="flex items-center gap-1.5 pt-1.5 border-t border-[#E8DB6C]/40">
        <i className="ri-user-line text-[9px] text-[#999999]"></i>
        <span className="text-[9px] text-[#999999]">{card.manager}</span>
        <span className="text-[9px] text-[#999999] ml-auto">{card.daysActive} дн. в работе</span>
      </div>
    </div>
  );
}

const COLUMN_WIDTH = 230;
const COLUMN_GAP = 12;
const VISIBLE_COLUMNS = 5;

export default function DealsKanbanWidget() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const totalCards = kanbanStages.reduce((sum, s) => sum + s.cards.length, 0);
  const totalAmount = kanbanStages.reduce((sum, s) => sum + s.cards.reduce((a, c) => a + c.amount, 0), 0);
  const totalStages = kanbanStages.length;

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollButtons();
    el.addEventListener("scroll", updateScrollButtons, { passive: true });
    return () => el.removeEventListener("scroll", updateScrollButtons);
  }, [updateScrollButtons]);

  const scrollBy = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = (COLUMN_WIDTH + COLUMN_GAP) * VISIBLE_COLUMNS;
    const target = direction === "left"
      ? Math.max(0, el.scrollLeft - scrollAmount)
      : Math.min(el.scrollWidth - el.clientWidth, el.scrollLeft + scrollAmount);
    el.scrollTo({ left: target, behavior: "smooth" });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Summary bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-4">
          <h2 className="text-[14px] font-semibold text-[#2D2D2D] flex items-center gap-2">
            <i className="ri-kanban-view text-[15px] text-[#D97706]"></i>
            Сделки
          </h2>
          <span className="text-[11px] text-[#666666]">{totalCards} сделок на {formatMoney(totalAmount)}</span>
          <span className="text-[11px] text-[#999999]">| {totalStages} стадий</span>
        </div>
        <div className="flex items-center gap-2">
          <button className="flex items-center gap-1 px-2 py-1 text-[11px] text-[#666666] hover:bg-[#FDF2B5] rounded cursor-pointer transition-colors">
            <i className="ri-filter-line text-[11px]"></i>
            Фильтр
          </button>
          <button className="flex items-center gap-1 px-2 py-1 text-[11px] text-[#666666] hover:bg-[#FDF2B5] rounded cursor-pointer transition-colors">
            <i className="ri-add-line text-[11px]"></i>
            Сделка
          </button>
        </div>
      </div>

      {/* Scroll navigation + Kanban board */}
      <div className="flex-1 flex items-stretch min-h-0">
        {/* Left scroll arrow */}
        <button
          onClick={() => scrollBy("left")}
          className={`flex-shrink-0 w-8 flex items-center justify-center rounded-l transition-all cursor-pointer ${
            canScrollLeft
              ? "bg-[#FFFDF5] hover:bg-[#FDF2B5] text-[#2D2D2D] border border-[#D5CA8A] border-r-0"
              : "bg-transparent text-[#CCCCCC] cursor-default"
          }`}
          disabled={!canScrollLeft}
        >
          <i className="ri-arrow-left-s-line text-[18px]"></i>
        </button>

        {/* Scrollable board */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-x-auto"
          style={{ scrollbarWidth: "thin", scrollbarColor: "#D5CA8A transparent" }}
        >
          <div className="flex gap-3 px-1">
            {kanbanStages.map((stage) => (
              <StageColumn key={stage.key} stage={stage} />
            ))}
          </div>
        </div>

        {/* Right scroll arrow */}
        <button
          onClick={() => scrollBy("right")}
          className={`flex-shrink-0 w-8 flex items-center justify-center rounded-r transition-all cursor-pointer ${
            canScrollRight
              ? "bg-[#FFFDF5] hover:bg-[#FDF2B5] text-[#2D2D2D] border border-[#D5CA8A] border-l-0"
              : "bg-transparent text-[#CCCCCC] cursor-default"
          }`}
          disabled={!canScrollRight}
        >
          <i className="ri-arrow-right-s-line text-[18px]"></i>
        </button>
      </div>

      {/* Pagination dots */}
      <div className="flex items-center justify-center gap-1.5 mt-3">
        {kanbanStages.map((stage, idx) => {
          const pageIndex = Math.floor(idx / VISIBLE_COLUMNS);
          const isFirstOfPage = idx % VISIBLE_COLUMNS === 0;
          if (!isFirstOfPage) return null;
          return (
            <button
              key={stage.key}
              onClick={() => {
                const el = scrollRef.current;
                if (!el) return;
                const target = pageIndex * (COLUMN_WIDTH + COLUMN_GAP) * VISIBLE_COLUMNS;
                el.scrollTo({ left: target, behavior: "smooth" });
              }}
              className="w-2 h-2 rounded-full bg-[#D5CA8A] hover:bg-[#C4B96A] cursor-pointer transition-colors"
              title={`Стадии ${pageIndex * VISIBLE_COLUMNS + 1}–${Math.min((pageIndex + 1) * VISIBLE_COLUMNS, totalStages)}`}
            />
          );
        })}
      </div>
    </div>
  );
}